import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {AiFillDelete, AiFillEdit} from 'react-icons/ai';
import {useNavigate} from 'react-router-dom';

import CustomTable from '../components/common/CustomTable';
import {SelectFieldHookForm} from '../components/ui/SelectField';
import InputField from '../components/ui/InputField';
import {invalidateQueryV2} from '../services/reactQuery';
import AlertModal from '../components/AlertModal';
import Button from '../components/ui/Button';
import {
  useDeleteQuestionMutation,
  useGetQuestionsQuery,
} from '../api/candidateTest';
import {difficultyLevel, difficultyLevelOptions} from '../utils/constants';
import SubjectDropdown from '../components/common/SubjectDropdown';
import BulkQuestionUpload from '../components/BulkQuestionUpload';

const Questions = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
  });
  const [deleteQuestion, setDeleteQuestion] = useState(null);

  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);

  const deleteQuestionMutation = useDeleteQuestionMutation();
  const {isLoading: isDeleteQuestionLoading} = deleteQuestionMutation;

  const {
    data: questionData,
    isFetching: isQuestionsLoading,
    refetch,
  } = useGetQuestionsQuery(formData);
  const {
    questionData: allQuestionsMain,
    totalItems = null,
    totalPages = null,
  } = questionData?.data || {questionData: [], totalItems: 0, totalPages: 0};

  const allQuestions = questionData?.data.length === 0 ? [] : allQuestionsMain;

  const {handleSubmit, control, watch, register} = useForm({
    mode: 'all',
  });

  const handleFormSubmission = async (values) => {
    const {search, difficulty, subject} = values;
    let payload = {search};

    if (difficulty?.value) payload = {...payload, difficulty: difficulty.value};

    if (subject?.value) payload = {...payload, subject: subject.value};

    // set pagination to page 1
    setFormData((prev) => ({
      ...prev,
      ...payload,
      page: 1,
    }));
  };

  const handleDeleteUser = (userId) => {
    // send array of ids which we want to delete
    // that's why passing as array
    deleteQuestionMutation.mutate([userId], {
      onSuccess: () => {
        setDeleteQuestion(null);
        toast.success('Question successfully removed.');
        refetch();
      },
      onError: (data) => toast.error(data.response.data.message),
    });
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  // if table has no data then change the page/
  // useEffect(() => {
  //   if (!isQuestionsLoading && !allQuestions.length && !error) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       page: prev.page === 1 ? 1 : prev.page - 1,
  //     }));
  //   }
  // }, [isQuestionsLoading, allQuestions, error]);

  const tHeadData = [
    {
      id: 1,
      keyIndex: 'question',
      label: 'Question',
      sort: true,
      width: '70%',
    },
    {
      id: 2,
      keyIndex: 'subject',
      label: 'Subject',
      width: '10%',
    },
    {
      id: 3,
      keyIndex: 'difficulty',
      label: 'Difficulty',
      render: (item) => difficultyLevel[item] || '-',
      width: '10%',
    },
    {
      id: 5,
      keyIndex: 'lastName',
      label: 'Action',
      width: '10%',
      render: (_, {_id}) => (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(`/edit-question/${_id}`)}
          >
            <AiFillEdit size={20} />
          </button>
          <button
            type="button"
            aria-label="remove candidate from job"
            onClick={() => setDeleteQuestion(_id)}
          >
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <BulkQuestionUpload
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        title="Upload bulk questions"
      />
      <AlertModal
        type="warning"
        isOpen={deleteQuestion}
        onClose={() => setDeleteQuestion(null)}
        title="Confirm this action"
        onCancel={() => setDeleteQuestion(null)}
        onOK={() => handleDeleteUser(deleteQuestion)}
        okLoading={isDeleteQuestionLoading}
      >
        Are you sure you want to delete this question?
      </AlertModal>
      <div className="px-8 py-6">
        <div className="flex items-center gap-2">
          <h3 className="text-2xl font-bold">
            All Questions
            {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <form className="py-5">
          <div className="flex justify-between gap-4">
            <div className="gap-4 grid grid-cols-3 self-center">
              <SubjectDropdown
                placeholder="Subject"
                control={control}
                name="subject"
                className="w-full max-w-60 bg-white"
              />

              <SelectFieldHookForm
                placeholder="Difficulty"
                control={control}
                name="difficulty"
                options={difficultyLevelOptions}
                className="w-full max-w-60 bg-white"
              />

              <InputField
                {...register('search')}
                type="search"
                placeholder="Search"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                className="py-3 px-5 whitespace-nowrap"
                onClick={() => setIsBulkUploadModalOpen(true)}
              >
                Bulk upload questions
              </Button>
              <Button
                className="py-3 px-5 whitespace-nowrap"
                onClick={() => navigate('/add-questions')}
              >
                Add questions
              </Button>
            </div>
          </div>
        </form>

        <div className="bg-[white] p-10 border">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={allQuestions}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isQuestionsLoading}
          />
        </div>
      </div>
    </>
  );
};

export default Questions;
